import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { ToastContext } from "./context";
import { useSendChatMessage } from "./api/mutations/useAiChat";
import {
  useGetConversationList,
  useGetConversationDetail,
  useGetAiUnitBalance,
} from "./api/queries/useAiChat";
import {
  useDeleteConversation,
  useUpdateConversationTitle,
} from "./api/mutations/useAiChat";
import { pathConstants } from "../routes/pathContants";

const CONVERSATION_LIST_KEY = ["aiConversationList"];
const MAX_TITLE_LENGTH = 255;
const GENERIC_BLOCK_REASON =
  "Your AI assistant is paused. Top up your credit to continue.";

// `syncWithUrl` keeps the assistant page's conversation in the address bar.
// The floating popover runs on top of other pages, so it opts out and holds
// the conversation in state; `isEnabled` keeps it from fetching until opened.
const useAiChatSession = ({ syncWithUrl = true, isEnabled = true } = {}) => {
  const { conversationId: urlConversationId } = useParams();
  const navigate = useNavigate();
  const [messageInput, setMessageInput] = useState("");
  const [activeConversationId, setActiveConversationId] = useState(
    urlConversationId || null,
  );
  const [localMessages, setLocalMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => window.innerWidth > 768,
  );
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  // The `usage` block that rode in on the last reply — the only source for the
  // daily meter, and the freshest view of the credit chips.
  const [usage, setUsage] = useState(null);
  const [blockMessage, setBlockMessage] = useState("");
  const [blockedCode, setBlockedCode] = useState("");
  const [dismissedWarnStep, setDismissedWarnStep] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const copyResetTimerRef = useRef(null);
  const unitsRefetchTimerRef = useRef(null);
  const lastUnitsRefetchRef = useRef(0);
  const staleBalanceDateRef = useRef(null);
  const appliedBalanceRef = useRef(null);
  const blockedCodeRef = useRef("");
  // Conversations the merchant renamed by hand — the agent's generated title
  // must never clobber those.
  const renamedIdsRef = useRef(new Set());
  const queryClient = useQueryClient();
  const triggerToast = useContext(ToastContext);
  const profile = useSelector(
    (state) => state.profileDetailsReducer.profileDetails,
  );

  blockedCodeRef.current = blockedCode;

  // Messages are labelled with the account holder's name, so a shared screen
  // shows who asked. Falls back to the login email, then to a neutral label.
  const senderName = useMemo(() => {
    const fullName = [profile?.first_name, profile?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    return fullName || profile?.email || "You";
  }, [profile]);

  // Sync URL param to state when navigating
  useEffect(() => {
    if (!syncWithUrl) return;
    const newId = urlConversationId || null;
    if (newId !== activeConversationId) {
      setActiveConversationId(newId);
      if (!newId) setLocalMessages([]);
    }
  }, [urlConversationId, syncWithUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: conversationList, isLoading: isLoadingConversations } =
    useGetConversationList({ isEnabled });

  const { data: conversationDetail, isLoading: isLoadingDetail } =
    useGetConversationDetail({
      id: activeConversationId,
      isEnabled: isEnabled && !!activeConversationId,
    });

  const {
    data: unitBalance,
    isLoading: isLoadingUnitBalance,
    isError: isUnitBalanceError,
    refetch: refetchUnitBalance,
  } = useGetAiUnitBalance({ isEnabled });

  const sendMessageMutation = useSendChatMessage();
  const deleteConversationMutation = useDeleteConversation();
  const updateTitleMutation = useUpdateConversationTitle();

  // Every reply — success or failure — carries the authoritative usage block,
  // so it decides both the meter and whether the composer is usable.
  const applyUsage = useCallback((nextUsage) => {
    if (!nextUsage) return;
    setUsage(nextUsage);

    if (nextUsage.limit_reached || nextUsage.can_use_ai === false) {
      setBlockMessage(nextUsage.blocked_reason || GENERIC_BLOCK_REASON);
      setBlockedCode(
        nextUsage.blocked_code ||
          (nextUsage.limit_reached
            ? "daily_limit_reached"
            : "insufficient_ai_credit"),
      );
    } else {
      setBlockMessage("");
      setBlockedCode("");
    }
  }, []);

  // Replies carry their own usage block, so the balance endpoint is only
  // called on mount, on focus (a top up) and when its figures are a day old.
  // The debounce keeps those to at most one call every 3s.
  const refreshUnitBalance = useCallback(() => {
    const sinceLastCall = Date.now() - lastUnitsRefetchRef.current;
    clearTimeout(unitsRefetchTimerRef.current);
    unitsRefetchTimerRef.current = setTimeout(
      () => {
        lastUnitsRefetchRef.current = Date.now();
        refetchUnitBalance();
      },
      Math.max(0, 3000 - sinceLastCall),
    );
  }, [refetchUnitBalance]);

  useEffect(() => () => clearTimeout(unitsRefetchTimerRef.current), []);

  // Free units reset at local midnight — figures from another day are stale.
  // Refetched at most once per stale date so this never becomes a poll.
  useEffect(() => {
    const balanceDate = unitBalance?.date;
    if (!balanceDate) return;
    const today = new Date().toLocaleDateString("en-CA");
    if (balanceDate !== today && staleBalanceDateRef.current !== balanceDate) {
      staleBalanceDateRef.current = balanceDate;
      refreshUnitBalance();
    }
  }, [unitBalance, refreshUnitBalance]);

  // A freshly fetched balance saying the assistant is usable clears a credit
  // block (the top-up path). It must not clear a daily-limit block — paying
  // does nothing there until midnight. Keyed on the balance object so a block
  // set moments ago is not undone by the balance already in cache.
  useEffect(() => {
    if (!unitBalance || unitBalance === appliedBalanceRef.current) return;
    appliedBalanceRef.current = unitBalance;

    if (
      unitBalance.can_use_ai === true &&
      blockedCodeRef.current !== "daily_limit_reached"
    ) {
      setBlockMessage("");
      setBlockedCode("");
    }
  }, [unitBalance]);

  // Load conversation messages when detail is fetched
  useEffect(() => {
    if (conversationDetail?.messages) {
      setLocalMessages(
        conversationDetail.messages.map((msg, index, all) => ({
          role: msg.role,
          content: msg.content,
          id: msg.id,
          // Every message carries a visible time, so a stored one is used when
          // the API sends it and the message is left un-stamped when it does
          // not — the row simply omits the time rather than inventing one.
          at: msg.created_at || msg.timestamp || null,
          // Regenerate re-asks the question the answer belongs to, so each
          // assistant turn remembers the user turn it followed.
          question:
            msg.role === "assistant"
              ? all[index - 1]?.content || ""
              : undefined,
          // Only the latest assistant message renders this as buttons/a date
          // picker — see the `isLatest` check where messages are rendered.
          interactive_prompt:
            msg.role === "assistant" ? msg.interactive_prompt || null : null,
        })),
      );
    }
  }, [conversationDetail]);

  // Grow the input field with its content, up to the CSS max-height
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = "auto";
    // scrollHeight excludes borders, which count towards the border-box height
    const borders = input.offsetHeight - input.clientHeight;
    input.style.height = `${input.scrollHeight + borders}px`;
  }, [messageInput]);

  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [localMessages, scrollToBottom]);

  // Simulate streaming effect for assistant response
  const simulateStreaming = useCallback((fullText, meta = {}) => {
    setIsStreaming(true);
    const words = fullText.split(" ");
    let currentIndex = 0;

    // Add empty assistant message
    setLocalMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: "",
        isStreaming: true,
        at: new Date().toISOString(),
        ...meta,
      },
    ]);

    const interval = setInterval(() => {
      currentIndex++;
      const partial = words.slice(0, currentIndex).join(" ");

      setLocalMessages((prev) => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (updated[lastIdx]?.role === "assistant") {
          updated[lastIdx] = {
            ...updated[lastIdx],
            content: partial,
            isStreaming: currentIndex < words.length,
          };
        }
        return updated;
      });

      if (currentIndex >= words.length) {
        clearInterval(interval);
        setIsStreaming(false);
      }
    }, 30);

    return () => clearInterval(interval);
  }, []);

  // Unknown or failed balance must never block chatting — only an explicit
  // `false` from the server, or a blocking reply, pauses the composer.
  const balanceBlocked = !usage && unitBalance?.can_use_ai === false;
  const isAiBlocked = Boolean(blockMessage) || balanceBlocked;
  const blockedReason =
    blockMessage || unitBalance?.blocked_reason || GENERIC_BLOCK_REASON;
  // Paying changes nothing while the daily cap is what is blocking, so the
  // top-up action belongs only to the credit block.
  const canTopUpForBlock = blockedCode !== "daily_limit_reached";

  // Credit chips read from the last reply when there is one; the balance
  // endpoint only seeds them before the first message and after a top up.
  const creditBalance = useMemo(() => {
    if (!usage) return unitBalance;
    return {
      ...(unitBalance || {}),
      date: usage.date,
      free_units: usage.free_units,
      free_units_per_day: usage.free_units_per_day,
      free_units_used: usage.free_units_used,
      main_units: usage.main_units,
      can_use_ai: usage.can_use_ai,
      blocked_reason: usage.blocked_reason,
    };
  }, [usage, unitBalance]);

  // Everything about the meter comes off the payload — never recomputed, and
  // never compared against a hardcoded limit or threshold.
  const dailyLimit = Number(usage?.daily_limit || 0);
  const hasDailyLimit = Boolean(usage) && dailyLimit > 0;
  const percentUsed = usage?.percent_used;
  // Warning returns after each further 10 points of usage
  const warnStep = Number.isFinite(percentUsed)
    ? Math.floor(percentUsed / 10)
    : null;
  const showLimitWarning =
    hasDailyLimit &&
    usage.near_limit === true &&
    usage.limit_reached !== true &&
    (dismissedWarnStep === null ||
      (warnStep !== null && warnStep > dismissedWarnStep));

  const dismissLimitWarning = useCallback(
    () => setDismissedWarnStep(warnStep ?? 0),
    [warnStep],
  );

  // Writes the agent's generated title into the cached list, adding the row
  // when the conversation is brand new. A hand-picked title always wins.
  const applyGeneratedTitle = useCallback(
    (id, title) => {
      if (!id || !title || renamedIdsRef.current.has(id)) return;

      queryClient.setQueryData(CONVERSATION_LIST_KEY, (list) => {
        const current = list || [];
        return current.some((convo) => convo.id === id)
          ? current.map((convo) =>
              convo.id === id ? { ...convo, title } : convo,
            )
          : [{ id, title }, ...current];
      });
    },
    [queryClient],
  );

  // Takes the message explicitly so a suggested prompt can be sent without
  // going through the composer's state first.
  const sendMessage = useCallback(
    async (rawMessage, { isRegenerate = false } = {}) => {
      const trimmed = (rawMessage || "").trim();
      if (!trimmed || isStreaming || isAiBlocked) return;

      // A regenerate re-asks a question that is already in the thread, so the
      // user turn is left where it is and the composer is not touched.
      if (!isRegenerate) {
        setLocalMessages((prev) => [
          ...prev,
          { role: "user", content: trimmed, at: new Date().toISOString() },
        ]);
        setMessageInput("");
      }

      // Add loading indicator
      setLocalMessages((prev) => [
        ...prev,
        { role: "assistant", content: "", isLoading: true },
      ]);

      try {
        const response = await sendMessageMutation.mutateAsync({
          message: trimmed,
          conversation_id: activeConversationId || null,
          stream: false,
        });

        const data = response?.data?.data;

        // Remove loading indicator
        setLocalMessages((prev) => prev.filter((msg) => !msg.isLoading));

        // The reply carries the metered figures, so no balance call is needed
        applyUsage(data?.usage);

        if (data?.success) {
          // Set conversation ID if this was a new conversation
          if (!activeConversationId && data.conversation_id) {
            setActiveConversationId(data.conversation_id);
            if (syncWithUrl)
              navigate(
                pathConstants.LUMI_AI_ASSISTANT + "/" + data.conversation_id,
                { replace: true },
              );
          }

          // The agent titles a conversation from its first message — take that
          // straight from the reply rather than refetching the list.
          applyGeneratedTitle(
            activeConversationId || data.conversation_id,
            data.title,
          );

          // Simulate streaming of the response
          simulateStreaming(data.response, {
            question: trimmed,
            interactive_prompt: data.interactive_prompt || null,
          });
        } else {
          setLocalMessages((prev) => [
            ...prev.filter((msg) => !msg.isLoading),
            {
              role: "assistant",
              content:
                data?.error || "Sorry, something went wrong. Please try again.",
              isError: true,
              at: new Date().toISOString(),
              question: trimmed,
            },
          ]);
        }
      } catch (error) {
        const status = error?.response?.status;
        const errorData = error?.response?.data;
        // Branch on the code, not the status, so a code added later cannot
        // fall into the wrong branch and offer a useless top up. Status is a
        // fallback only when no code came back at all.
        const code =
          errorData?.data?.code ||
          (status === 402
            ? "insufficient_ai_credit"
            : status === 429
              ? "daily_limit_reached"
              : "");
        const isBlocked = Boolean(code) && (status === 402 || status === 429);

        setLocalMessages((prev) => prev.filter((msg) => !msg.isLoading));

        // Even a rejection carries usage, so the meter stays accurate
        applyUsage(errorData?.data?.usage);

        if (isBlocked) {
          // The reason goes in the banner above the composer, and the typed
          // message stays put so it survives the block.
          setBlockMessage(errorData?.detail || GENERIC_BLOCK_REASON);
          setBlockedCode(code);
          setMessageInput(trimmed);
        } else {
          setLocalMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "Sorry, something went wrong. Please try again.",
              isError: true,
              at: new Date().toISOString(),
              question: trimmed,
            },
          ]);
        }
      }
    },
    [
      isStreaming,
      isAiBlocked,
      activeConversationId,
      syncWithUrl,
      navigate,
      sendMessageMutation,
      simulateStreaming,
      applyUsage,
      applyGeneratedTitle,
    ],
  );

  const handleSendMessage = useCallback(
    () => sendMessage(messageInput),
    [sendMessage, messageInput],
  );

  // Clicking one of the starter questions on the empty state sends it as-is.
  // Follow-up chips under an answer take the same path.
  const handleSuggestedPrompt = useCallback(
    (prompt) => sendMessage(prompt),
    [sendMessage],
  );

  // Drops the answer at `index` and asks its question again. Only the local
  // thread is rewritten — the conversation stored server side keeps the
  // original turn, so a reload shows both attempts.
  const handleRegenerateMessage = useCallback(
    (index) => {
      const target = localMessages[index];
      if (!target || target.role !== "assistant") return;

      const question =
        target.question || localMessages[index - 1]?.content || "";
      if (!question) return;

      setLocalMessages((prev) => prev.filter((_, i) => i !== index));
      sendMessage(question, { isRegenerate: true });
    },
    [localMessages, sendMessage],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const handleNewChat = useCallback(() => {
    setActiveConversationId(null);
    setLocalMessages([]);
    setMessageInput("");
    // A block is not cleared here: a new chat does not restore credit or reset
    // the daily cap. Only a fresh usage/balance payload lifts it.
    if (syncWithUrl)
      navigate(pathConstants.LUMI_AI_ASSISTANT, { replace: true });
    inputRef.current?.focus();
  }, [navigate, syncWithUrl]);

  const handleTopUpCredit = useCallback(() => {
    navigate(pathConstants.SETTINGS_PLATFORM_CREDIT_TOPUP);
  }, [navigate]);

  const handleSelectConversation = useCallback(
    (id) => {
      if (id === activeConversationId) return;
      setActiveConversationId(id);
      setLocalMessages([]);
      if (syncWithUrl)
        navigate(pathConstants.LUMI_AI_ASSISTANT + "/" + id, { replace: true });
      queryClient.invalidateQueries(["aiConversationDetail", id]);
      // Auto-close sidebar on mobile after selecting a conversation
      if (window.innerWidth <= 768) {
        setIsSidebarOpen(false);
      }
    },
    [activeConversationId, queryClient, navigate, syncWithUrl],
  );

  const handleDeleteConversation = useCallback(
    async (id) => {
      try {
        await deleteConversationMutation.mutateAsync(id);
        if (activeConversationId === id) {
          handleNewChat();
        }
      } catch (error) {
        // silently fail
      }
    },
    [activeConversationId, deleteConversationMutation, handleNewChat],
  );

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  // ---------- Renaming ----------

  const conversations = useMemo(
    () => conversationList || [],
    [conversationList],
  );

  const activeConversationTitle = useMemo(() => {
    if (!activeConversationId) return "";
    // The list holds the optimistic rename, so it wins over the fetched detail
    const listed = conversations.find(
      (convo) => convo.id === activeConversationId,
    );
    return listed?.title || conversationDetail?.title || "";
  }, [activeConversationId, conversations, conversationDetail]);

  const startRename = useCallback((id, currentTitle) => {
    setRenamingId(id);
    setRenameDraft(currentTitle || "");
  }, []);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameDraft("");
  }, []);

  const updateRenameDraft = useCallback(
    (value) => setRenameDraft((value || "").slice(0, MAX_TITLE_LENGTH)),
    [],
  );

  const commitRename = useCallback(async () => {
    const id = renamingId;
    const nextTitle = renameDraft.trim();
    const currentTitle = (
      conversations.find((convo) => convo.id === id)?.title || ""
    ).trim();

    setRenamingId(null);
    setRenameDraft("");

    // Nothing to save: blank or unchanged closes the editor without a request
    if (!id || !nextTitle || nextTitle === currentTitle) return;

    renamedIdsRef.current.add(id);

    try {
      await updateTitleMutation.mutateAsync({ id, title: nextTitle });
    } catch (error) {
      // The optimistic title was already rolled back by the mutation
      renamedIdsRef.current.delete(id);

      if (error?.response?.status === 404) {
        queryClient.setQueryData(CONVERSATION_LIST_KEY, (list) =>
          (list || []).filter((convo) => convo.id !== id),
        );
        triggerToast("Conversation no longer exists", "error");
        if (activeConversationId === id) handleNewChat();
        return;
      }

      // 400s come back as a raw DRF field error rather than the usual envelope
      const fieldError = error?.response?.data?.title?.[0];
      triggerToast(
        fieldError ||
          error?.response?.data?.detail ||
          "Could not rename conversation",
        "error",
      );
    }
  }, [
    renamingId,
    renameDraft,
    conversations,
    updateTitleMutation,
    queryClient,
    triggerToast,
    activeConversationId,
    handleNewChat,
  ]);

  const handleCopyMessage = useCallback(
    async (content, index) => {
      try {
        await navigator.clipboard.writeText(content);
        setCopiedMessageIndex(index);
        clearTimeout(copyResetTimerRef.current);
        copyResetTimerRef.current = setTimeout(
          () => setCopiedMessageIndex(null),
          2000,
        );
      } catch (error) {
        triggerToast("Unable to copy message", "error");
      }
    },
    [triggerToast],
  );

  useEffect(() => () => clearTimeout(copyResetTimerRef.current), []);

  return {
    messageInput,
    setMessageInput,
    localMessages,
    isStreaming,
    isSidebarOpen,
    isSending: sendMessageMutation.isLoading,
    isLoadingConversations,
    isLoadingDetail,
    conversationList: conversations,
    activeConversationId,
    activeConversationTitle,
    copiedMessageIndex,
    unitBalance: creditBalance,
    isLoadingUnitBalance,
    isUnitBalanceError,
    usage,
    hasDailyLimit,
    showLimitWarning,
    dismissLimitWarning,
    isAiBlocked,
    blockedReason,
    canTopUpForBlock,
    renamingId,
    renameDraft,
    updateRenameDraft,
    startRename,
    cancelRename,
    commitRename,
    maxTitleLength: MAX_TITLE_LENGTH,
    senderName,
    messagesEndRef,
    inputRef,
    conversationDetail: conversationDetail || {},
    handleSendMessage,
    handleSuggestedPrompt,
    handleRegenerateMessage,
    handleKeyDown,
    handleNewChat,
    handleSelectConversation,
    handleDeleteConversation,
    handleTopUpCredit,
    handleCopyMessage,
    toggleSidebar,
  };
};

export default useAiChatSession;
